import _ from 'lodash';
import template from './monitoring.html';


export default {
  name: 'monitoringComponent',
  template,
  controller: MonitoringController,
  controllerAs: 'MonitoringCtrl'
};

/* @ngInject */
function MonitoringController(
  $scope,
  $sails,
  growl,
  $timeout,
  $filter,
  $log,
  MonitoringService,
  monitoringGridFactory
) {
  const vm = this;
  /// ////////////////
  // Local Variables//
  /// ////////////////
  const defaultGridSettings = {
    paginationPageSizes: [10, 20, 50, 100, 1000], // eslint-disable-line no-magic-numbers
    enableGridMenu: true,
    enableFiltering: true,
    showGridFooter: true,
    showColumnFooter: false,
    enableRowSelection: false,
    enableSelectAll: false,
    multiSelect: true,
    modifierKeysToMultiSelect: true,
    enableRowHeaderSelection: false,
    enableColumnResizing: true,
    enableColumnMenus: false,
    exporterMenuPdf: false,
    exporterMenuCsv: true,
    exporterMenuExcel: false,
    exporterMenuAllData: true,
    exporterMenuVisibleData: true,
    exporterMenuSelectedData: true
  };
  let generateTimeout;
  const ttl = 5000;
  const ONE_SECOND = 1000;

  /// ////////////////
  // Public Variables/
  /// ////////////////
  vm.disableGenerateReport = true;
  vm.digiGrid = createGrid('digi', 'digiGrid');
  vm.rcuGrid = createGrid('rcu', 'rcuGrid');
  vm.cameraGrid = createGrid('camera', 'cameraGrid');
  vm.tankGrid = createGrid('tank', 'tankGrid');
  vm.cameraStatusGrid = createGrid('cameraStatus', 'cameraStatusGrid');
  vm.restartedGrid = createGrid('cameraEvent', 'restartedGrid');

  vm.missingVideoGrid = createGrid('cameraEvent', 'missingVideoGrid');
  vm.missingVideoGrid.columnDefs.push({
    field: 'event.data.count',
    displayName: 'Total Sequence Count',
    enableHiding: false,
    enableCellEdit: false,
    type: 'number'
  }, {
    field: 'event.data.reason',
    displayName: 'Reason',
    enableHiding: false,
    enableCellEdit: false
  });

  /// ////////////////
  // Public Functions/
  /// ////////////////
  vm.generateReport = generateReport;

  /// //////////////////////////////
  // Hoisted Function Declarations//
  /// //////////////////////////////
  init();

  function setCreatedAt(createdAt) {
    if (vm.reportDate) {
      growl.info('A new report is coming in!', { ttl });
    }
    vm.reportDate = createdAt;
    const timeSinceReport = Date.now() - new Date(createdAt);
    vm.disableGenerateReport = timeSinceReport < vm.reportTtl * ONE_SECOND;
    if (vm.disableGenerateReport) {
      if (generateTimeout) {
        $timeout.cancel(generateTimeout);
      }
      generateTimeout = $timeout(() => {
        vm.disableGenerateReport = false;
      }, (vm.reportTtl * ONE_SECOND) - timeSinceReport + 1);
    }
  }

  function setPendingEvents(pending) {
    vm.pendingEvents = pending;
  }

  function fillDigiGrid(digis) {
    const dcinfo = vm.devicecloud;
    vm.ipsDown = {};
    vm.digisDown = [];

    _.forEach(digis, ({ ip, name }) => {
      if (ip) {
        const obj = {
          name,
          ip
        };
        if (dcinfo[ip]) {
          obj.lastConnect = dcinfo[ip].last_connect;
          obj.lastDisconnect = dcinfo[ip].last_disconnect;
        }
        vm.digisDown.push(obj);
        vm.ipsDown[ip] = true;
      }
      vm.digiGrid.data = vm.digisDown;
    });
  }

  function fillRcuGrid(rcus) {
    vm.rcuGrid.data = _.filter(rcus, rcu => !vm.ipsDown[rcu.ip]);
  }

  function fillCamerasGrid(cameras) {
    vm.cameraGrid.data = _.filter(cameras, camera => !vm.ipsDown[camera.settings.externalIp]);
  }

  function fillMissingVideoGrid(result) {
    vm.missingVideoGrid.data = result;
  }

  function fillStatusGrid(result) {
    vm.cameraStatusGrid.data = result;
  }

  function fillRestartedGrid(result) {
    vm.restartedGrid.data = result;
  }

  function fillRegularUpdateGrid(result) {
    vm.tankGrid.data = result;
  }

  function setDevicecloud(result) {
    vm.devicecloud = result;
  }

  function handleComplete() {
    growl.info('The report generation has finished. Please wait for it...', { ttl });
  }

  function handleDistributed() {
    growl.info('The new report is here!', { ttl });
  }

  async function init() {
    ({ reportTtl: vm.reportTtl } =  await MonitoringService.reportTtl());

    const { events: pendingEvents } = await MonitoringService.pendingEvents();
    setPendingEvents(pendingEvents);

    const { createdAt } = await MonitoringService.createdAt();
    setCreatedAt(createdAt);

    setDevicecloud(await MonitoringService.devicecloud());

    fillDigiGrid(await MonitoringService.failed('digi'));
    fillRcuGrid(await MonitoringService.failed('rcu'));
    fillCamerasGrid(await MonitoringService.failed('cameras'));
    fillMissingVideoGrid(await MonitoringService.eventInfo('cameras/cameraMissingVideo'));
    fillStatusGrid(await MonitoringService.eventInfo('cameras/status'));
    fillRestartedGrid(await MonitoringService.eventInfo('cameras/cameraReachable'));
    fillRegularUpdateGrid(await MonitoringService.eventInfo('tanks/regularUpdate'));

    setUpListeners();
  }

  function setUpListeners() {
    MonitoringService.joinRoom()
      .catch(err => $log.error('Could not join monitoring room', err));

    $scope.$on('$destroy', handleDestroy);

    $sails.on('createdAt', setCreatedAt);
    $sails.on('pending:events', setPendingEvents);

    $sails.on('cameras/cameraMissingVideo', fillMissingVideoGrid);
    $sails.on('cameras/status', fillStatusGrid);
    $sails.on('cameras/cameraReachable', fillRestartedGrid);
    $sails.on('tanks/regularUpdate', fillRegularUpdateGrid);

    $sails.on('devicecloud', setDevicecloud);
    $sails.on('digi:failed', fillDigiGrid);
    $sails.on('rcu:failed', fillRcuGrid);
    $sails.on('cameras:failed', fillCamerasGrid);

    $sails.on('complete', handleComplete);
    $sails.on('distributed', handleDistributed);
  }

  async function generateReport() {
    const growlMessage = growl.info('Requesting new report', { disableCloseButton: true });
    vm.disableGenerateReport = true;
    await MonitoringService.newReport()
      .then(res => growl.success(`A report is being generated for ${$filter('date')(res.message, 'medium')}`,
        { ttl }))
      .catch(err => growl.error(err, { title: 'Could not generate new report: ' }));
    growlMessage.destroy();
  }

  function onGridRegister(gridName) {
    return (gridApi) => {
      vm[`${gridName}API`] = gridApi;
      const millitime = 700;
      $timeout(() => {
        // this call fixes the issue with the grid rendering smaller on page load
        vm[`${gridName}API`].core.handleWindowResize();
      }, millitime);
    };
  }

  function createGrid(gridType, gridName) {
    const defs = monitoringGridFactory(gridType);
    defs.onRegisterApi = onGridRegister(gridName);
    return _.defaults(defs, defaultGridSettings);
  }

  function handleDestroy() {
    $sails.off('createdAt', setCreatedAt);
    $sails.off('pending:events', setPendingEvents);

    $sails.off('cameras/cameraMissingVideo', fillMissingVideoGrid);
    $sails.off('cameras/status', fillStatusGrid);
    $sails.off('cameras/cameraReachable', fillRestartedGrid);
    $sails.off('tanks/regularUpdate', fillRegularUpdateGrid);

    $sails.off('devicecloud', setDevicecloud);
    $sails.off('digi:failed', fillDigiGrid);
    $sails.off('rcu:failed', fillRcuGrid);
    $sails.off('cameras:failed', fillCamerasGrid);

    $sails.off('complete', handleComplete);
    $sails.off('distributed', handleDistributed);

    MonitoringService.leaveRoom();
  }
}
